import ExportarCsv from '@/global/components/DataGrid/components/CustomGridToolBar/ExportarCsv'
import {
    GridButton,
    GridMenuButton,
    GridMenuItem,
} from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { useFilterLocaisCentroStore } from '@/global/components/Selects'
import { Alert, AlertTitle, CircularProgress, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { Link } from 'global/components/Link'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    FileTextIcon,
    FileUserIcon,
    HistoryIcon,
    SettingsIcon,
    UploadIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import {
    SaldoFolha,
    integracaoFolha,
} from 'service/api/endpoints/integracao-folha'
import { toast } from 'sonner'
import { useStore } from 'store'

const columns: GridColDefPro[] = [
    {
        field: 'codigoRubrica',
        headerName: 'Código',
        width: 80,
        type: 'number',
    },
    {
        field: 'nomeRubrica',
        headerName: 'Rubrica',
        minWidth: 180,
    },
    {
        field: 'matricula',
        headerName: 'Matrícula',
        width: 100,
        type: 'number',
    },
    {
        field: 'Funcionario',
        headerName: 'Funcionário',
        minWidth: 300,
        flex: 1,
        valueGetter: ({ row }) => row.Funcionario.nome,
    },
    {
        field: 'valor',
        headerName: 'Valor',
        width: 120,
    },
    {
        field: 'dataRescisao',
        headerName: 'Data de rescisão',
        type: 'date',
        hide: true,
        valueGetter: ({ row }) => row.Funcionario.dtRescisao,
    },
]

export default function FolhaPagamento() {
    const [competencia, setCompetencia] = useCompetencia()
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const [sortModel, setSortModel, sort] = useDefaultSortModel(columns)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [isImprimindo, setIsImprimindo] = useState(false)
    const empresaId = useStore(state => state.companyId)
    const navigate = useNavigate()

    const {
        data = [],
        isError,
        isLoading,
    } = integracaoFolha.useFetch({
        queryParams: {
            empresaId,
            competencia,
        },
        enabled: empresaId > 0,
        onError: ({ message }) => {
            setErrorMessage(message as string)
            toast.error(message)
        },
        retry: 0,
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = data.filter(useFilter)

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={['Integrações', 'Folha de pagamento']}
                >
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SelectCompetencia
                            onChange={setCompetencia}
                            value={competencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    error={isError}
                    loading={isLoading}
                    rows={rows}
                    columns={columns}
                    ToolbarProps={{
                        hideExportButton: true,
                        showQuickFilter: true,
                    }}
                    components={{
                        Toolbar: () => [
                            <GridMenuButton
                                startIcon={
                                    isImprimindo ? (
                                        <CircularProgress
                                            color="primary"
                                            size={20}
                                        />
                                    ) : (
                                        <UploadIcon />
                                    )
                                }
                                label="Exportar"
                                key="exportar"
                                disabled={isImprimindo}
                            >
                                {hasPermission([
                                    'create',
                                    'FolhaDePagamento',
                                ]) && (
                                    <GridMenuItem
                                        startIcon={
                                            <FileTextIcon className="mr-2" />
                                        }
                                        onClick={() => navigate('exportar-txt')}
                                        label="Exportar txt"
                                    />
                                )}
                                <GridMenuItem
                                    startIcon={
                                        <FileUserIcon className="mr-2" />
                                    }
                                    onClick={() => {
                                        if (!rows.length)
                                            return toast.warning(
                                                'Não há dados para exportar',
                                            )
                                        else {
                                            setIsImprimindo(true)
                                            toast.info(
                                                'Seu pdf está sendo gerado.',
                                            )
                                            integracaoFolha
                                                .pdf({
                                                    data: sort(
                                                        rows.filter(
                                                            new FilterModelPro(
                                                                filterModel,
                                                                columns,
                                                            ).filter,
                                                        ),
                                                    ).map(item => item.id),
                                                    queryParam: {
                                                        empresaId,
                                                        competencia,
                                                    },
                                                })
                                                .catch(e => {
                                                    toast.error(
                                                        'Erro ao gerar pdf. ' +
                                                            e.response.data
                                                                .message,
                                                    )
                                                    console.error(e)
                                                })
                                                .finally(() => {
                                                    setIsImprimindo(false)
                                                })
                                        }
                                    }}
                                    label="Exportar PDF"
                                />
                                <ExportarCsv mi fileName="integracao-folha" />
                            </GridMenuButton>,
                            hasPermission(['read', 'Rubricas']) && (
                                <GridMenuButton
                                    href="/cadastros/rubricas"
                                    startIcon={<SettingsIcon />}
                                    label="Configurar"
                                    key="configurar"
                                />
                            ),
                            <GridButton
                                href="historico"
                                startIcon={<HistoryIcon />}
                                label="Histórico"
                                key="historico"
                            />,
                            <div style={{ flex: 1 }} key="flex" />,
                        ],
                        ErrorOverlay: () => {
                            return (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="100%"
                                >
                                    <Alert
                                        severity="error"
                                        sx={{
                                            width: 550,
                                        }}
                                    >
                                        <AlertTitle>{errorMessage}</AlertTitle>
                                        {errorMessage.includes('vale') && (
                                            <p>
                                                Para gerar a integração da folha
                                                corretamente, é necessário que o
                                                vale alimentação esteja
                                                configurado, para isso, acesse{' '}
                                                <Link href="/relatorios/vale-alimentacao/configuracao?voltar=/integracoes/folha-pagamento">
                                                    Vale Alimentação
                                                </Link>{' '}
                                            </p>
                                        )}
                                        {errorMessage.includes(
                                            'Empresa não possui código',
                                        ) && (
                                            <p>
                                                Para gerar a integração da folha
                                                corretamente, é necessário que a
                                                empresa possua um código de
                                                integração com a folha de
                                                pagamento, para isso, acesse{' '}
                                                <Link
                                                    href={`/cadastros/empresas/${empresaId}/editar?aba=ponto#codEmpresaFolha`}
                                                >
                                                    Empresas
                                                </Link>{' '}
                                                e informe o código de
                                                integração.
                                            </p>
                                        )}
                                    </Alert>
                                </Box>
                            )
                        },
                        Footer: () => (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                px={2}
                            >
                                Funcionários com prefixo (*) possuem CPF
                                inválido
                            </Typography>
                        ),
                    }}
                />
            </ContentGuide.Card>
            <Outlet
                context={{
                    data:
                        rows?.filter(
                            new FilterModelPro(filterModel, columns).filter,
                        ) ?? [],
                    filterModel,
                    competencia,
                }}
            />
        </ContentGuide>
    )
}

export function useFolhaPagamentoOutletContext() {
    return useOutletContext<{
        data?: SaldoFolha[]
        competencia: string
    }>()
}
